import '@babel/polyfill';
import { LicenseManager } from '@ag-grid-enterprise/core'; // eslint-disable-line
import { createInstance, OptimizelyProvider } from '@optimizely/react-sdk'; // eslint-disable-line
import * as Sentry from '@sentry/react';
// imports modules from common package
import {
	Authentication,
	Chatbot,
	Data,
	Error as ErrorModule,
	FeatureFlags,
	Layout,
	Modal,
	RateAdvisor,
	Redirect,
	Toast,
	UI,
	Utils,
} from 'common';
import { isObject } from 'lodash';
import React, { Component } from 'react';
import { createRoot } from 'react-dom/client';
import MailView from './components/MailView.component';
import MailCommunication from './components/utils/MailCommunication';

Sentry.init({
	dsn: 'https://2bf0cdb2c7121a2c231715a123b5bbb1@o106853.ingest.us.sentry.io/4509831239499776',
	// Setting this option to true will send default PII data to Sentry.
	// For example, automatic IP address collection on events
	sendDefaultPii: true,
	environment: process.env.NODE_ENV === 'production' ? 'mail' : 'mail-dev',
});

const { ModalContainer } = Modal;
const {
	Analytics,
	Auth0Util,
	Session,
	StyleLoader,
	Util,
	Navigation,
	CustStorage,
	BillingPlans,
	withLayoutAndModalContexts,
} = Utils;
const { ChatbotInitializer, SierraInitializer } = Chatbot;
const { InitializeLaunchDarkly } = FeatureFlags;
const { Spinner } = UI;
const { SignIn } = Authentication;
const { ToastProvider } = Toast;
const { CalculatedData } = Data;
const { CarrierServices } = RateAdvisor;
const { EndiciaRedirect } = Redirect;

LicenseManager.setLicenseKey(__AG_GRID_LICENSE__);

export default class App extends Component {
	constructor() {
		super();
		// hardcoded Settings.Page type for now
		const Page = 'mail';

		// Check if on Endicia domain - skip auth calls but still load styles
		const redirectToStamps = Util.isEndiciaDomain();

		const SSOError = Util.getQueryParamByName('sso_error_code');

		const data = {
			InternalAccountInfo: true,
		};

		this.state = {
			mask: false,
			showSignIn: false,
			data,
			ssoError: SSOError,
			auth0Error: false,
			optimizelyClient: null,
			stylesLoaded: false,
			// custom error page for auth0 error page
			auth0CustomError: Util.isInRoute('autherror') === true,
		};

		Session.init(
			{ Page, fbAppId: 794486702036528 }, // hardcoded facebook fbAppId for now (Got from marketing team)
			this.mailTimeout
		);

		MailCommunication.init({
			resetTimeout: Session.resetTimeout,
			triggerTimeout: Session.triggerTimeout,
		});

		// Skip verifyAuthAndLoad for Endicia domain, but still load styles
		if (redirectToStamps) {
			StyleLoader.loadTheme(() => {
				// Directly set state in constructor (setState not allowed here)
				this.state.mask = false;
				this.state.stylesLoaded = true;
			});
		} else {
			Session.verifyAuthAndLoad(data, this.sessionLoadCallback);
		}
	}

	sessionLoadCallback = async () => {
		const sessionData = Session.getData();
		const { isSignedIn, settings } = sessionData;
		const optimizelyClient = createInstance({ sdkKey: settings.OptimizelyKey });

		document.title = `Stamps.com | Home`; // TODO check to see if this messes up rate advisor
		ErrorModule.ErrorService.init();
		StyleLoader.loadTheme(this.setState({ mask: true, stylesLoaded: true }));

		const completeAuth = Util.isInRoute('completeAuth') === true;

		if (!isSignedIn && completeAuth) {
			const completeAuthResponse = await Auth0Util.completeAuth();

			if (completeAuthResponse?.isSuccessful === true) {
				Navigation.redirect(completeAuthResponse.url);
				return;
			} else {
				this.setState({
					auth0Error: true,
				});
			}
		}

		// Initialize data services after page loads
		if (isSignedIn) {
			// make a call to carrier-services api
			const shipDate =
				Util.getQueryParamByName('dt') ||
				new Date().toISOString().split('T')[0];

			const response = await CarrierServices.getCarrierServices(shipDate);
			const data = MailCommunication.isSuccessful(response)
				? Util.toCamelCase(response.data)
				: null;

			CalculatedData.init(data);

			// Send this to the top of the event loop to ensure it runs
			// after the page loads for better user experience.
			// TODO check to see if we can remove this and rate advisor works without it.
			setTimeout(async () => {
				await CustStorage.init();
				await BillingPlans.init();
			}, 0);
		}

		Analytics.set(sessionData);

		const LDProvider = await InitializeLaunchDarkly();

		this.setState(({ showSignIn }) => ({
			// do not force signIn view if optionalAuth is true
			showSignIn: !isSignedIn,

			// hide spinner after Session ajax response comes back
			mask: false,
			optimizelyClient,
			analyticsMetadata: Analytics.metadata,
			LDProvider,
		}));
	};

	/**
	 * Initializes Sierra chatbot for Mail view with Header component shown.
	 */
	initializeChatbot = showHeader => {
		if (showHeader) {
		}
	};

	signInCallback = async ({ status }) => {
		const { data } = this.state;

		if (status) {
			this.setState({
				mask: true,
				showSignIn: false,
			});

			ChatbotInitializer.resetChatMessengerUserPrefs();
			Session.verifyAuthAndLoad(data, this.sessionLoadCallback);
		}
	};

	mailTimeout = () => {
		const { optionalAuth } = this.state;

		if (!optionalAuth) {
			SierraInitializer.logout();
			ChatbotInitializer.logout();
			if (Auth0Util.isEligibleForAuth0()) {
				Session.signOut(async () => {
					Auth0Util.signOut();
				});
			} else {
				this.setState({
					showSignIn: true,
				});
			}
		}
	};

	render() {
		// Redirect to Stamps.com when on Endicia domain - must be the very first thing
		const redirectToStamps = Util.isEndiciaDomain();

		if (redirectToStamps) {
			return <EndiciaRedirect />;
		}

		const {
			mask,
			showSignIn,
			ssoError,
			optimizelyClient,
			analyticsMetadata,
			LDProvider,
			stylesLoaded,
			auth0Error,
			auth0CustomError,
		} = this.state;
		const { isInWebStore } = Session.getSettings();
		const sessionData = Session.getData();
		const { isSignedIn } = sessionData;
		const shouldShowAuth0SignIn =
			Auth0Util.isEligibleForAuth0() &&
			auth0Error === false &&
			auth0CustomError === false;

		const MailViewWithLayoutAndModal = withLayoutAndModalContexts(MailView);

		if (!stylesLoaded) {
			return null;
		} else if (mask) {
			return <Spinner msg="Loading" />;
		}

		if (showSignIn) {
			if (shouldShowAuth0SignIn) {
				Auth0Util.launchSignInPage();
				return null;
			}

			return (
				<Layout.Viewport shouldScroll={showSignIn}>
					<ModalContainer>
						<ToastProvider>
							<SignIn
								SSOError={ssoError}
								signInCallback={this.signInCallback}
								isInWebStore={isInWebStore}
								isAuth0Error={auth0Error}
								showAuth0CustomErrorPage={auth0CustomError}
							/>
						</ToastProvider>
					</ModalContainer>
				</Layout.Viewport>
			);
		} else if (
			isSignedIn &&
			isObject(analyticsMetadata.visitor) &&
			analyticsMetadata.visitor.id
		) {
			return (
				<ToastProvider>
					<OptimizelyProvider
						optimizely={optimizelyClient}
						user={{
							id: analyticsMetadata.visitor.id.toString(),
							attributes: analyticsMetadata.visitor,
						}}
					>
						{LDProvider ? (
							<LDProvider>
								<ModalContainer>
									<MailViewWithLayoutAndModal
										initializeChatbot={this.initializeChatbot}
									/>
								</ModalContainer>
							</LDProvider>
						) : (
							<ModalContainer>
								<MailViewWithLayoutAndModal
									initializeChatbot={this.initializeChatbot}
								/>
							</ModalContainer>
						)}
					</OptimizelyProvider>
				</ToastProvider>
			);
		}

		return (
			<ToastProvider>
				<MailViewWithLayoutAndModal />
			</ToastProvider>
		);
	}
}

const root = createRoot(document.getElementById('root'));
root.render(<App />);
